import { FC, useState } from 'react';
import { getQueryKey } from '@trpc/react-query';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { Link, useHistory } from 'react-router-dom';
import { styled, useTheme } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import SkipNext from '@mui/icons-material/SkipNext';
import SkipPrevious from '@mui/icons-material/SkipPrevious';
import FileDownload from '@mui/icons-material/FileDownload';
import { Row, Column } from 'further-ui/components/Layout';
import Menu from 'further-ui/components/Menu';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import ColorIndicator from 'further-ui/components/ColorIndicator';
import { useDisclosure } from 'further-ui/hooks';
import { CalendarDay } from 'further-ui/utils';
import { PeriodReviewStatus } from 'further-types/lp/accounting-period';
import {
  LpInvestorUpdateStatus,
  LpInvestorUpdateType,
} from 'further-types/lp/investor-update';
import {
  createLpPeriodEndReviewDetailRoute,
  LP_PERIOD_END_REVIEW,
  LP_INVESTOR_UPDATES,
  createPeriodEndReportAddRoute,
  createPeriodEndReportEditRoute,
} from 'adminConstants/routes';
import { useLayoutContext } from 'contexts/LayoutContext';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import Page from 'components/new/Page';
import PeriodCard from 'components/new/PeriodCard';
import api from 'lib/trpcClient';
import DownloadInvestorUpdates from '../DownloadInvestorUpdates';

const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.palette.secondary.main,
}));

const InfoOutlined = styled(InfoOutlinedIcon)({
  fontSize: '16px',
});

type AccountingPeriod = {
  periodStart: CalendarDay;
  periodEnd: CalendarDay;
  investorUpdate: {
    id: string;
    type: LpInvestorUpdateType;
    title: string;
    investorPdfUploads: Array<{
      investorId: string;
      path: string;
      previewUrl: string;
    }>;
    investors: Array<{
      id: string;
      fullName: string;
    }>;
  } | null;
};

const PeriodEndReport: FC = () => {
  const [accountingPeriod, setAccountingPeriod] =
    useState<AccountingPeriod | null>(null);
  const theme = useTheme();
  const history = useHistory();
  const { selectedVehicleId } = useLayoutContext();
  const vehicleId = selectedVehicleId ?? '';
  const skipReportingPeriodDisclosure = useDisclosure<{ id: string }>();

  const [periods] = api.accountingPeriod.listPeriodsWithReport.useSuspenseQuery(
    { vehicleId },
  );

  const listPeriodsWithReportQueryKey = getQueryKey(
    api.accountingPeriod.listPeriodsWithReport,
    { vehicleId },
    'query',
  );
  const { mutate: setReportSkipped, isPending } =
    api.accountingPeriod.setReportSkipped.useMutation(
      useHandleTrpcMutation()({
        invalidations: [listPeriodsWithReportQueryKey],
      }),
    );

  const pendingReportingPeriods = periods.filter(
    (p) =>
      !p.reportSkippedAt &&
      (!p.investorUpdate ||
        p.investorUpdate?.status === LpInvestorUpdateStatus.Draft),
  );
  const hasPendingReportingPeriods = pendingReportingPeriods.length > 0;

  const publishedReportingPeriods = periods.filter(
    (p) =>
      p.reportSkippedAt ||
      p.investorUpdate?.status === LpInvestorUpdateStatus.Published,
  );
  const hasPublishedReportingPeriods = publishedReportingPeriods.length > 0;

  return (
    <Page
      tabTitle="Investor Updates: Period End Report"
      heading="Period-end report"
      goBackAction={LP_INVESTOR_UPDATES}
      spacing="lg"
      withHeadingDivider
    >
      <Column spacing="lg">
        {!hasPendingReportingPeriods && !hasPublishedReportingPeriods ? (
          <Typography variant="body2">No reporting periods found.</Typography>
        ) : null}
        {hasPendingReportingPeriods ? (
          <Typography variant="body2">Pending reporting periods</Typography>
        ) : null}
        {pendingReportingPeriods.map((period) => {
          const dateRange = `${period.periodStart.toReadable()} – ${period.periodEnd.toReadable()}`;
          const isClosed = period.reviewStatus === PeriodReviewStatus.Approved;

          return (
            <PeriodCard
              key={period.periodStart.toString()}
              icon={
                <ColorIndicator
                  padded
                  rounded
                  color={
                    isClosed
                      ? theme.palette.error.main
                      : theme.palette.warning.light
                  }
                />
              }
              primaryLabel={<Typography variant="h6">{dateRange}</Typography>}
              secondaryLabel={
                <Typography variant="body2">
                  {isClosed ? (
                    `Period closed ${period.approvedAt?.toReadable()} and due for update`
                  ) : (
                    <Row spacing="xxs" centered>
                      The selected period is awaiting final review and close{' '}
                      <StyledLink
                        to={
                          period.id
                            ? createLpPeriodEndReviewDetailRoute(period.id)
                            : LP_PERIOD_END_REVIEW
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Start period-end review
                      </StyledLink>
                      <Tooltip
                        title="The report can be drafted but not submitted until period-end review is completed."
                        placement="top-start"
                      >
                        <InfoOutlined />
                      </Tooltip>
                    </Row>
                  )}
                </Typography>
              }
              actions={
                <Row spacing="xs" centered>
                  <Button
                    variant="contained"
                    color="primary"
                    loading={isPending}
                    onClick={() => {
                      history.push(
                        period.investorUpdate
                          ? createPeriodEndReportEditRoute(
                              period.investorUpdate.id,
                            )
                          : createPeriodEndReportAddRoute(period.id ?? ''),
                      );
                    }}
                  >
                    {period.investorUpdate ? 'Edit' : 'Start'}
                  </Button>
                  <Menu
                    handleIcon={MoreHoriz}
                    alignWithIconRightEdge
                    gap="sm"
                    items={[
                      {
                        icon: SkipNext,
                        label: 'Skip reporting period',
                        onClick: () => {
                          skipReportingPeriodDisclosure.stageAction({
                            id: period.id ?? '',
                          });
                        },
                      },
                    ]}
                  />
                </Row>
              }
            />
          );
        })}
        {hasPendingReportingPeriods && hasPublishedReportingPeriods ? (
          <Divider />
        ) : null}
        {hasPublishedReportingPeriods ? (
          <Typography variant="body2">Published reporting periods</Typography>
        ) : null}
        {publishedReportingPeriods.map((period) => {
          const dateRange = `${period.periodStart.toReadable()} – ${period.periodEnd.toReadable()}`;
          const isReportSkipped = !!period.reportSkippedBy;

          return (
            <PeriodCard
              key={period.periodStart.toString()}
              icon={
                <ColorIndicator
                  padded
                  rounded
                  color={
                    isReportSkipped
                      ? theme.palette.error.main
                      : theme.palette.success.light
                  }
                />
              }
              primaryLabel={<Typography variant="h6">{dateRange}</Typography>}
              secondaryLabel={
                <Typography variant="body2">
                  {isReportSkipped
                    ? `Skipped ${period.reportSkippedAt?.toReadable()} by ${
                        period.reportSkippedByLabel
                      }`
                    : `Published ${period.investorUpdate?.reportDate?.toReadable()} by ${
                        period.investorUpdate?.publishedByLabel
                      }`}
                </Typography>
              }
              actions={
                <Row spacing="xs" centered>
                  {!isReportSkipped ? (
                    <Button
                      variant="outlined"
                      color="primary"
                      loading={isPending}
                      onClick={() => {
                        history.push(
                          period.investorUpdate
                            ? createPeriodEndReportEditRoute(
                                period.investorUpdate.id,
                              )
                            : createPeriodEndReportAddRoute(period.id ?? ''),
                        );
                      }}
                    >
                      {period.investorUpdate ? 'Edit' : 'Start'}
                    </Button>
                  ) : null}
                  {
                    <Menu
                      handleIcon={MoreHoriz}
                      alignWithIconRightEdge
                      gap="sm"
                      items={[
                        isReportSkipped
                          ? {
                              icon: SkipPrevious,
                              label: 'Unskip reporting period',
                              onClick: () => {
                                if (!vehicleId || !period.id) return;
                                setReportSkipped({
                                  vehicleId,
                                  id: period.id,
                                  skipped: false,
                                });
                              },
                            }
                          : {
                              icon: FileDownload,
                              label: 'Download PDFs',
                              disabled:
                                !period.investorUpdate?.investorPdfUploads
                                  .length,
                              onClick: () => {
                                setAccountingPeriod(period);
                              },
                            },
                      ]}
                    />
                  }
                </Row>
              }
            />
          );
        })}
      </Column>
      {accountingPeriod ? (
        <DownloadInvestorUpdates
          investorUpdate={accountingPeriod.investorUpdate}
          vehicleId={vehicleId}
          reportId={accountingPeriod.investorUpdate?.id ?? ''}
          onClose={() => setAccountingPeriod(null)}
        />
      ) : null}
      <ConfirmDialog
        maxWidth="sm"
        onClose={skipReportingPeriodDisclosure.onClose}
        open={skipReportingPeriodDisclosure.isOpen}
        title="Skip reporting period"
        content={
          <Typography variant="body2">
            Proceeding will move this period into the{' '}
            <em>Completed reporting periods</em> section without a report being
            processed. This can be undone at any point.
          </Typography>
        }
        actions={{
          confirm: {
            label: 'Skip period',
            onClick: () => {
              if (!vehicleId) return;
              setReportSkipped({
                vehicleId,
                id: skipReportingPeriodDisclosure.actionProps.id,
                skipped: true,
              });
              skipReportingPeriodDisclosure.onClose();
            },
          },
          cancel: {
            label: 'Cancel',
            onClick: skipReportingPeriodDisclosure.onClose,
          },
        }}
      />
    </Page>
  );
};

export default PeriodEndReport;
